import { useBlockFlags } from '@ContractBuilder/modules/block/context/context';
import { BlockContent } from '@ContractBuilder/modules/block-content';
import { useBlockActions } from '@ContractBuilder/modules/block-edit';
import { ctxIsPDF } from '@ContractBuilder/rules/block/is-ctx';
import { useEntityStore } from '@ContractBuilder/store';
import { canChange } from '@helpers/canChange';
import type { VariationsTabsContextLiteValue } from '@providers/variations-tabs';
import { useVariationInView, VariationsTabsContextLite } from '@providers/variations-tabs';
import type { BlockVariation } from '@root/@types/database';
import type { ResourceBlock } from '@root/@types/types';
import { upliftVariation } from '@root/helpers/variations';
import { getDataTestId } from '@root/src/utils/element-testid-selectors';
import { useDeepCompareMemo } from '@src/hooks';

import { VariationTabLite } from '../components/VariationTabLite';

import '../index.css';

interface VariationsProviderLiteProps {
  block: ResourceBlock;
}

export const VariationsSelectorLiteController = ({ block }: VariationsProviderLiteProps) => {
  const { updateBlock } = useBlockActions();
  const submission = useEntityStore((store) => store.submission);
  const canEdit = canChange(submission?.status);

  const {
    block: processedBlock,
    context,
    isHighlighted,
    mentionValues,
    details: { isEditing, isVisible, isParentEntityEditable },
  } = useBlockFlags();

  const { variations, id: blockId } = processedBlock || {};
  const isPDFRender = ctxIsPDF(context);

  const {
    currentVariationIdx,
    setCurrent,
    variationInView,
    variationsCount,
    selectedVariationId,
    visibilityDetails,
    hasLogic,
  } = useVariationInView(processedBlock);

  const select = (newVariationId: string) => {
    const isAlreadySelected = newVariationId === block?.selectedVariationId;

    if (isAlreadySelected) {
      return;
    }

    const payload = upliftVariation(block, newVariationId);
    updateBlock({ ...payload, id: blockId });
  };

  const blockPreview = useDeepCompareMemo(() => {
    const variation = variations?.[currentVariationIdx];
    if (!variation) return processedBlock;

    const { title, content, linkedDatapoints = [] } = variation;
    return { ...processedBlock, title, content, linkedDatapoints };
    // eslint-disable-next-line -- Run only when current variation changes
  }, [processedBlock, currentVariationIdx]);

  const value: VariationsTabsContextLiteValue = useDeepCompareMemo(
    () => ({
      currentVariationIdx,
      hasLogic,
      canEdit,
      select,
      selectedVariationId,
      setCurrent,
      variationInView,
      variationsCount,
      visibilityDetails,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- block-derived members are covered by deep-comparing processedBlock
    [processedBlock, currentVariationIdx, canEdit],
  );
  const dataTestId = getDataTestId('variations.tabsRow', block?.title);

  return (
    <VariationsTabsContextLite.Provider value={value}>
      <div
        key={`collapsible-item-${blockId}`}
        className="appearance--variations-tab-row-lite"
        data-testid={dataTestId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-info-200/75 flex w-full overflow-x-auto">
          {variations?.map((entry: BlockVariation) => (
            <VariationTabLite key={entry.id} variation={entry} />
          ))}
        </div>
      </div>
      <BlockContent
        block={blockPreview}
        disableMentionHighlight={!isParentEntityEditable && !isPDFRender}
        mentionValues={mentionValues}
        isEditing={isEditing}
        isHighlighted={isHighlighted}
        isInCreateMode={isEditing && !block?.id}
        isPdfRender={false}
        isVisible={isVisible}
      />
    </VariationsTabsContextLite.Provider>
  );
};
